import { useState } from "react";
import { Plus, FileText, Loader2, Pencil, Trash2, Paperclip } from "lucide-react";
import toast from "react-hot-toast";
import { useVehicleDocuments } from "../hooks/useVehicleDocuments";
import DocumentFormModal from "./DocumentFormModal";

const DOC_TYPE_LABELS = {
  rc:        "RC",
  insurance: "Insurance",
  permit:    "Permit",
  puc:       "PUC",
  fitness:   "Fitness",
};

function fmtDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function expiryStatus(iso) {
  if (!iso) return { label: "No expiry set", color: "#6B7280", bg: "#F3F4F6" };
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  if (days < 0)   return { label: "Expired",             color: "#DC2626", bg: "#FEF2F2" };
  if (days <= 30) return { label: `Expires in ${days}d`, color: "#D97706", bg: "#FFFBEB" };
  return { label: `Valid till ${fmtDate(iso)}`,          color: "#16A34A", bg: "#F0FDF4" };
}

export default function DocumentsPanel({ vehicles }) {
  const { documents, loading, addDocument, updateDocument, removeDocument, getFileLink } = useVehicleDocuments();
  const [showForm,   setShowForm]   = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [openingId,  setOpeningId]  = useState(null);

  function openAdd()  { setEditingDoc(null); setShowForm(true); }
  function openEdit(d) { setEditingDoc(d);   setShowForm(true); }

  function vehicleLabel(vehicleId) {
    const v = vehicles.find(v => v.id === vehicleId);
    return v ? (v.nickname || v.plateNumber) : "Unknown vehicle";
  }

  async function handleSubmit(payload) {
    if (editingDoc) await updateDocument(editingDoc.id, payload);
    else            await addDocument(payload);
  }

  async function handleDelete(doc) {
    if (!window.confirm(`Remove this ${DOC_TYPE_LABELS[doc.docType]} document?`)) return;
    try {
      await removeDocument(doc.id);
      toast.success("Document removed");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to remove document");
    }
  }

  async function handleViewFile(doc) {
    setOpeningId(doc.id);
    try {
      const url = await getFileLink(doc.id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to open file");
    } finally {
      setOpeningId(null);
    }
  }

  return (
    <main style={{ flex: 1, overflow: "auto", background: "#F9FAFB" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "28px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#111827" }}>Documents</div>
            <div style={{ fontSize: 13, color: "#6B7280", marginTop: 2 }}>RC, insurance, permit, PUC, and fitness certificates with expiry tracking</div>
          </div>
          <button
            onClick={openAdd}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "#4F46E5", border: "none", borderRadius: 8,
              padding: "0 16px", height: 38, cursor: "pointer",
              fontSize: 13.5, fontWeight: 700, color: "#fff",
            }}
          >
            <Plus size={15} /> Add Document
          </button>
        </div>

        {loading && (
          <div style={{ padding: 60, textAlign: "center", color: "#9CA3AF", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /> Loading documents…
          </div>
        )}

        {!loading && documents.length === 0 && (
          <div style={{
            background: "#fff", border: "1px dashed #D1D5DB", borderRadius: 14,
            padding: "48px 24px", textAlign: "center",
          }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#374151", marginBottom: 6 }}>No documents yet</div>
            <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 18 }}>Add your vehicles' RC, insurance, permit, or PUC to track expiry dates.</div>
            <button
              onClick={openAdd}
              style={{ background: "#4F46E5", border: "none", borderRadius: 8, padding: "0 18px", height: 38, color: "#fff", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}
            >
              <Plus size={14} style={{ marginRight: 6, verticalAlign: -2 }} /> Add Document
            </button>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {documents.map(doc => {
            const expiry = expiryStatus(doc.expiryDate);
            return (
              <div key={doc.id} style={{
                background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12,
                padding: "16px 18px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                  background: "#EEF2FF", color: "#4F46E5",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <FileText size={18} />
                </div>

                <div style={{ flex: "1 1 220px", minWidth: 180 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: "#111827" }}>
                    {DOC_TYPE_LABELS[doc.docType]} — {vehicleLabel(doc.vehicleId)}
                  </div>
                  <div style={{ display: "flex", gap: 12, marginTop: 4, flexWrap: "wrap" }}>
                    {doc.docNumber && (
                      <span style={{ fontSize: 12, color: "#6B7280" }}>{doc.docNumber}</span>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                    <span style={{
                      display: "inline-block", fontSize: 10.5, fontWeight: 700,
                      color: expiry.color, background: expiry.bg, borderRadius: 99, padding: "2px 9px",
                    }}>
                      {expiry.label}
                    </span>
                    {!doc.fileName && (
                      <span style={{ fontSize: 11.5, color: "#9CA3AF" }}>No file attached</span>
                    )}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  {doc.fileName && (
                    <button
                      onClick={() => handleViewFile(doc)}
                      disabled={openingId === doc.id}
                      title="View attached file"
                      style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: 7, height: 32, padding: "0 10px", cursor: "pointer", color: "#4F46E5", display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600 }}
                    >
                      {openingId === doc.id ? <Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} /> : <Paperclip size={13} />}
                      View file
                    </button>
                  )}
                  <button
                    onClick={() => openEdit(doc)}
                    title="Edit document"
                    style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: 7, width: 32, height: 32, cursor: "pointer", color: "#6B7280", display: "flex", alignItems: "center", justifyContent: "center" }}
                  ><Pencil size={13} /></button>
                  <button
                    onClick={() => handleDelete(doc)}
                    title="Remove document"
                    style={{ background: "none", border: "1px solid #FECACA", borderRadius: 7, width: 32, height: 32, cursor: "pointer", color: "#DC2626", display: "flex", alignItems: "center", justifyContent: "center" }}
                  ><Trash2 size={13} /></button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showForm && (
        <DocumentFormModal
          document={editingDoc}
          vehicles={vehicles}
          onClose={() => setShowForm(false)}
          onSubmit={handleSubmit}
        />
      )}
    </main>
  );
}
